import { Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { CountryISO, PhoneNumberFormat, SearchCountryField } from 'ngx-intl-tel-input';
import { MenuItem, MessageService } from 'primeng/api';
import { fadeInAnimation } from 'src/app/Animations';
import { ApiCallService } from 'src/app/Services/api-call.service';
import { GlobalService } from 'src/app/Services/global.service';
import { emailValidator } from '../../Directive/email-validator.directive';
import { DataShareService } from 'src/app/Services/data-share.service';
import { Subscription } from 'rxjs';
import { CryptoService } from 'src/app/Services/crypto.service';
const timezone_country = require('../../../assets/country-timezone.js');

interface IUser {
  name: string;
  companyName: string;
  email: string;
  phoneNumber: number;
  password: string;
  countryLists: any;
  industriesList: any;
  monthlyVolumeList: any;
}

interface IUserCountry {
  countryLists: any;
  industriesList: any;
  monthlyVolumeList: any;
  timeZone: string;
}

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
  animations: [fadeInAnimation],
  host: { '[@fadeInAnimation]': '' },
})
export class RegisterComponent implements OnInit, OnDestroy {
  timeZoneList: any = [];
  personalForm!: FormGroup;
  personalCountryForm!: FormGroup;

  separateDialCode = false;
  preferredCountries: CountryISO[] = [CountryISO.India, CountryISO.Kuwait];
  PhoneNumberFormat = PhoneNumberFormat;
  SearchCountryField = SearchCountryField;
  selectedCountryFlag: any;
  isOtpBox: boolean = false;
  user: IUser = {} as IUser;
  userCountry: IUserCountry = {} as IUserCountry;
  isVerified: boolean = false;
  otp: string[] = ['', '', '', '', '', ''];
  isResendDisabled: boolean = false; // Controls Resend link
  countdown: number = 30; // Countdown timer in seconds
  private countdownInterval: any; // To store interval reference

  constructor(
    public toaster: MessageService,
    private callMethod: ApiCallService,
    public Global: GlobalService,
    public router: Router,
    private dataShare: DataShareService,
    private CryptoService: CryptoService
  ) { }

  activeIndex = 0;
  page1 = true;
  page2 = false;
  page3 = false;
  selectedCountry: any;
  selectedTimeZone: any;
  selectedIndustries: any;
  selectedMonthlyVolume: any;
  monthlyVolume: any;
  countryList: any[] = [];
  industries: any[] = [];
  isValidate = false;
  pageTitle = '';
  flag: any = [];
  public subscription: Subscription = new Subscription();

  @ViewChildren('otp0, otp1, otp2, otp3, otp4, otp5') otpInputs!: QueryList<ElementRef>;

  ngOnInit(): void {
    this.selectedCountryFlag = CountryISO.Kuwait;
    this.countryList = Object.values(timezone_country.getAllCountries());
    this.pageTitle = this.Global.getLocalStorage({ key: 'pageTitle' });

    this.personalForm = new FormGroup({
      name: new FormControl('', [
        Validators.required,
        Validators.minLength(1),
        Validators.maxLength(250),
        Validators.pattern(/^[\p{L}\p{N}\s'-]{1,250}$/u)
      ]),
      companyName: new FormControl('', [
        Validators.required,
        Validators.maxLength(250),
        Validators.pattern(/^[\p{L}\p{N}\s'-]{1,250}$/u)
      ]),
      phoneNumber: new FormControl('', [Validators.required]),
      email: new FormControl('', [
        Validators.required,
        Validators.minLength(1),
        Validators.maxLength(250),
        emailValidator(),
      ]),
      password: new FormControl('', [
        Validators.required,
        Validators.pattern(
          '(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[$@$!%*?&])[A-Za-zd$@$!%*?&].{8,}'
        ),
      ]),
    });

    this.personalCountryForm = new FormGroup({
      countryLists: new FormControl('', [Validators.required]),
      industriesList: new FormControl('', [Validators.required]),
      monthlyVolumeList: new FormControl('', [Validators.required]),
      timeZone: new FormControl('', [Validators.required]),
    });

    this.industries = [
      { name: 'Beverage' },
      { name: 'Building / Construction' },
      { name: 'Prepared Meals' },
      { name: 'Restaurant' },
      { name: 'Restaurant Delivery Service' },
      { name: 'Retail / eCommerce' },
      { name: 'Shipping / Postal' },
      { name: 'Third Party Logistics' },
      { name: 'Waste Management / Recycling' },
      { name: 'Concierge' },
      { name: 'Courier' },
      { name: 'Desserts / Novelties' },
      { name: 'Grocery / Produce' },
      { name: 'Laundry / Drycleaning' },
      { name: 'Moving / Storage' },
      { name: 'Pharmacy / Medical' },
      { name: 'Other' },
    ];

    this.monthlyVolume = [
      { name: '0 - 100' },
      { name: '101 - 2,500' },
      { name: '2,501 - 5,000' },
      { name: '5,001 - 12,500' },
      { name: '12,501 - 25,000' },
      { name: '25,001 - 50,000' },
      { name: '50,000+' },
    ];

    this.subscription = this.dataShare.getData().subscribe((result: any) => {
      if (result.function == "isLoadedGetCompanyDetailsApi" && result.data != "") {
        this.Global.companyDetails.companyId = this.Global.companyDetails.companyId;
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval); // Clean up interval on component destroy
    }
  }

  get name() { return this.personalForm.get('name')!; }
  get companyName() { return this.personalForm.get('companyName')!; }
  get email() { return this.personalForm.get('email')!; }
  get phoneNumber() { return this.personalForm.get('phoneNumber')!; }
  get password() { return this.personalForm.get('password')!; }
  get industriesList() { return this.personalCountryForm.get('industriesList')!; }
  get countryLists() { return this.personalCountryForm.get('countryLists')!; }
  get monthlyVolumeList() { return this.personalCountryForm.get('monthlyVolumeList')!; }
  get timeZone() { return this.personalCountryForm.get('timeZone')!; }

  validate(formName: string) {
    if (formName === 'personalForm') {
      if (this.personalForm.invalid || !this.isVerified) {
        this.isValidate = true;
        Object.values(this.personalForm.controls).forEach((control) =>
          control.markAsTouched()
        );
        if (!this.isVerified) {
          this.toaster.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Please verify your email before continuing.',
          });
        }
        return;
      } else {
        this.signUpCall();
      }
    }

    if (formName === 'countryData') {
      if (this.personalCountryForm.invalid) {
        Object.values(this.personalCountryForm.controls).forEach((control) =>
          control.markAsTouched()
        );
        return;
      } else {
        this.editUserCall();
      }
    }
  }

  loginBtn = false;

  // Method to send OTP (called when clicking "Verify Email")
  async sendOtp() {
    if (this.email.invalid) {
      this.toaster.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please enter a valid email address.',
      });
      return;
    }

    const encryptedEmail = await this.CryptoService.encryptAES(this.personalForm.value.email);
    const reqObj = {
      email: encryptedEmail,
    };
    this.Global.setHeaderEncryptData(
      this.personalForm.value.email + this.Global.companyDetails.companyId
    );
    this.callMethod.sendOtpCall(reqObj).subscribe((result: any) => {
      if (result.status) {
        this.isOtpBox = true;
        this.toaster.add({
          severity: 'success',
          summary: 'Success',
          detail: 'OTP sent to your email.',
        });
      } else {
        this.toaster.add({
          severity: 'error',
          summary: 'Error',
          detail: result.message,
        });
        this.isOtpBox = false;
      }
    });
  }

  // Method to verify OTP
  async verifyOtp() {
    const otpCode = this.otp.join('');
    if (otpCode.length !== 6) {
      this.toaster.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please enter a valid 6-digit OTP.',
      });
      return;
    }

    const encryptedEmail = await this.CryptoService.encryptAES(this.personalForm.value.email);
    const encryptedOtp = await this.CryptoService.encryptAES(otpCode);
    const reqObj = {
      email: encryptedEmail,
      otp: encryptedOtp,
    };

    this.Global.setHeaderEncryptData(
      this.personalForm.value.email + otpCode + this.Global.companyDetails.companyId
    );

    this.callMethod.verifyOtpCall(reqObj).subscribe((result: any) => {
      if (result.status) {
        this.isVerified = true;
        this.isOtpBox = false;
        this.toaster.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Email verified successfully.',
        });
      } else {
        this.toaster.add({
          severity: 'error',
          summary: 'Error',
          detail: result.message,
        });
      }
    });
  }

  async signUpCall() {
    if (!this.isVerified) {
      this.toaster.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please verify your email before continuing.',
      });
      return;
    }

    this.Global.getLocalStorage({ key: 'JwtToken' });
    const timeZone = timezone_country.getTimezonesForCountry(
      this.personalForm.value.phoneNumber.countryCode
    );

    this.user = this.personalForm.value;
    const userPhoneNumber = this.Global.getUserPhoneNumber(
      this.user.phoneNumber
    );
    this.loginBtn = true;

    const encryptedPassword = await this.CryptoService.encryptAES(this.user.password);
    const encryptedEmail = await this.CryptoService.encryptAES(this.user.email);
    const encryptedPhone = await this.CryptoService.encryptAES(userPhoneNumber);
    const reqObj = {
      fullName: this.user.name,
      companyName: this.user.companyName,
      email: encryptedEmail,
      phone: encryptedPhone,
      password: encryptedPassword,
      timezone: timeZone[0].name,
    };

    this.Global.setHeaderEncryptData(
      this.user.email + userPhoneNumber + this.Global.companyDetails.companyId
    );

    this.callMethod.signUpCall(reqObj).subscribe((result: any) => {
      if (result.status) {
        this.Global.setLocalStorage({
          key: 'JwtToken',
          value: result.token,
        });
        this.Global.setLocalStorage({
          key: 'userId',
          value: result.data.userId,
        });
        this.Global.setLocalStorage({
          key: 'type',
          value: result.data.type,
        });
        this.activeIndex = 1;
        this.page1 = false;
        this.page2 = true;
      } else {
        this.toaster.add({
          severity: 'error',
          summary: 'Error',
          detail: result.message,
        });
      }
      this.loginBtn = false;
    });
  }

  editUserCall() {
    this.user = this.personalForm.value;
    this.loginBtn = true;
    this.userCountry = this.personalCountryForm.value;


    const reqObj = {
      industryType: this.userCountry.industriesList.name,
      industryMonthlyVolume: this.userCountry.monthlyVolumeList.name,
      timezone: this.personalCountryForm.value.timeZone.name,
      userId:
        this.Global.getLocalStorage({ key: 'userId' }) ||
        this.Global.userDetails.userId,
      currency: this.registerCountryCode[0].currency,
      country: this.registerCountryCode[0].name,
    };



    this.Global.setHeaderEncryptData(
      reqObj.userId + this.Global.companyDetails.companyId
    );
    this.callMethod.editUserCall(reqObj).subscribe((result: any) => {
      if (result.status) {
        this.Global.setLocalStorage({
          key: 'JwtToken',
          value: result.token,
        });
        this.Global.setLocalStorage({
          key: 'type',
          value: result.data.type,
        });
        this.Global.userDetails.JwtToken = result.token;
        this.Global.userDetails.fullName = result.data.fullName;
        this.Global.userDetails.parentUser = result.data.parentUser;
        this.Global.settingInfo.timeZone = result.data.timezone;
        this.Global.userDetails.phone = result.data.phone;
        this.Global.userDetails.userCompanyName = result.data.companyName;
        this.Global.userDetails.email = result.data.email;
        this.Global.userDetails.type = result.data.type;

        this.router.navigate(['dashboard/manage']);
      } else {
        this.toaster.add({
          severity: 'error',
          summary: 'Error',
          detail: result.message,
        });
      }
      this.loginBtn = false;
    });
  }

  onInput(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    this.otp[index] = value.slice(0, 1); // Allow only 1 character

    // Focus next input if exists
    if (value && index < 5) {
      this.otpInputs.toArray()[index + 1].nativeElement.focus();
    }
  }

  onKeyDown(event: KeyboardEvent, index: number) {
    const input = event.target as HTMLInputElement;

    if (event.key === 'Backspace') {
      if (input.value) {
        this.otp[index] = '';
      } else if (index > 0) {
        this.otpInputs.toArray()[index - 1].nativeElement.focus();
      }
    }
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') || '';
    const digits = pasted.replace(/\D/g, '').slice(0, 6).split('');

    digits.forEach((digit, i) => {
      if (i < 6) {
        this.otp[i] = digit;
      }
    });

    setTimeout(() => {
      const next = this.otpInputs.toArray()[digits.length] || this.otpInputs.last;
      next.nativeElement.focus();
    }, 0);
  }
  registerCountryCode: any[] = [];
  selectedCountries(selectedCountry: any) {
    this.registerCountryCode = this.Global.countryCodeJson.filter(
      (obj: any) => obj.name === selectedCountry.name
    );
    this.timeZoneList = timezone_country.getTimezonesForCountry(
      selectedCountry.id
    );
  }

  // Method to resend OTP with countdown
  async resendOtp() {
    if (this.email.invalid) {
      this.toaster.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please enter a valid email address.',
      });
      return;
    }

    this.isResendDisabled = true;
    this.countdown = 30; // Reset countdown to 30 seconds

    // Start countdown timer
    this.countdownInterval = setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        clearInterval(this.countdownInterval);
        this.isResendDisabled = false; // Re-enable Resend link
      }
    }, 1000);

    // Clear previous OTP inputs
    this.otp = ['', '', '', '', '', ''];
    const encryptedEmail = await this.CryptoService.encryptAES(this.personalForm.value.email);

    // Call sendOtp API
    const reqObj = {
      email: encryptedEmail,
    };
    this.Global.setHeaderEncryptData(
      this.personalForm.value.email + this.Global.companyDetails.companyId
    );
    this.callMethod.sendOtpCall(reqObj).subscribe((result: any) => {
      if (result.status) {
        this.toaster.add({
          severity: 'success',
          summary: 'Success',
          detail: 'OTP resent to your email.',
        });
      } else {
        this.toaster.add({
          severity: 'error',
          summary: 'Error',
          detail: result.message,
        });
        this.isResendDisabled = false; // Re-enable Resend link on error
        clearInterval(this.countdownInterval);
      }
    });
  }

}